import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useState,
} from 'react';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

type SeriesVisibility = Record<string, boolean>;

type HighchartsSyncContextType = {
  seriesVisibility: SeriesVisibility;
  toggleSeries: (id: string) => void;
};

export const HighchartsSyncContext = createContext<HighchartsSyncContextType>({
  seriesVisibility: {},
  toggleSeries: () => {},
});

/**
 * Keeps series visibility in sync across multiple charts.
 * Used by the screenshot feature so filtering one chart is reflected in other
 * charts wrapped by this provider.
 */
export function HighchartsSyncProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [seriesVisibility, setSeriesVisibility] = useState<SeriesVisibility>(
    {},
  );

  const toggleSeries = useCallback((id: string) => {
    setSeriesVisibility((prev) => ({ ...prev, [id]: !(prev[id] ?? true) }));
  }, []);

  return (
    <HighchartsSyncContext.Provider value={{ seriesVisibility, toggleSeries }}>
      {children}
    </HighchartsSyncContext.Provider>
  );
}

export const SyncedHighchartsReact = forwardRef<
  HighchartsReact.RefObject,
  React.ComponentProps<typeof HighchartsReact>
>((props, ref)=>{
  const { seriesVisibility, toggleSeries } = useContext(HighchartsSyncContext);

  const syncedOptions = {
    ...props.options,
    series: (props.options?.series ?? []).map(
      (series: Highcharts.SeriesOptionsType & { id?: string }) => {
        if (!series.id) return series;
        return {
          ...series,
          visible: seriesVisibility[series.id] ?? true,
          events: {
            ...(series as any).events,
            legendItemClick() {
              toggleSeries(series.id);
              return false;
            },
          },
        };
      },
    ),
  };

  return <HighchartsReact {...props} ref={ref} options={syncedOptions} />;
});
